import { Component, Input, Output, EventEmitter, ViewChild, OnInit } from '@angular/core';
import { BaseComponent } from '../base/base.component';
import { PurchasedLicensesInfo } from '../data/purchased-licenses-info';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '../auth.service';
import { OrganizationService } from '../organization.service';
import { RedirectService } from '../redirect.service';
import { Router } from '@angular/router';
import { DataService } from '../data/data.service';
import { Location } from '@angular/common';
import { LicenseFilteringService } from '../services/license-filtering.service';

interface LicenseChangeSummary {
  purchaseCount: number;
  licenseCount: number;
  totalDollarAmount: number;
  arrDollarAmount: number;
}

@Component({
  selector: 'app-license-change-analysis-modal',
  templateUrl: './license-change-analysis-modal.component.html',
  styleUrls: ['./license-change-analysis-modal.component.css']
})
export class LicenseChangeAnalysisModalComponent extends BaseComponent implements OnInit {

  @Input() organizationId: number;
  @Input() organizationDescription: string = '';
  @Input() allLicenses: PurchasedLicensesInfo[] = [];
  @Output() dataRefresh = new EventEmitter<void>();

  @ViewChild('modal') modal: any;

  startDate: Date;
  endDate: Date;
  loading: boolean = false;
  
  startDateLicenses: PurchasedLicensesInfo[] = [];
  endDateLicenses: PurchasedLicensesInfo[] = [];
  
  startDateSummary: LicenseChangeSummary = { purchaseCount: 0, licenseCount: 0, totalDollarAmount: 0, arrDollarAmount: 0 };
  expiredLicenses: PurchasedLicensesInfo[] = [];
  expiredSummary: LicenseChangeSummary = { purchaseCount: 0, licenseCount: 0, totalDollarAmount: 0, arrDollarAmount: 0 };
  newLicenses: PurchasedLicensesInfo[] = [];
  newSummary: LicenseChangeSummary = { purchaseCount: 0, licenseCount: 0, totalDollarAmount: 0, arrDollarAmount: 0 };
  endDateSummary: LicenseChangeSummary = { purchaseCount: 0, licenseCount: 0, totalDollarAmount: 0, arrDollarAmount: 0 };

  constructor(
    protected route: ActivatedRoute,
    protected authService: AuthService,
    protected organizationService: OrganizationService,
    protected redirectService: RedirectService,
    protected router: Router,
    protected dataService: DataService,
    protected location: Location,
    private modalService: NgbModal,
    private licenseFilteringService: LicenseFilteringService
  ) {
    super(route, authService, organizationService, redirectService, router, dataService, location);
  }

  ngOnInit(): void {
    // Ensure organizationId is set, default to 422 if null
    if (!this.organizationId) {
      this.organizationId = 422;
    }
    this.initializeDates();
  }

  private initializeDates(): void {
    // Set end date to current date
    this.endDate = new Date();
    
    // Set start date to last Monday (week or more prior to current date)
    this.startDate = this.getLastMonday();
    
    // Ensure both dates are valid Date objects
    if (!(this.startDate instanceof Date)) {
      this.startDate = new Date(this.startDate);
    }
    if (!(this.endDate instanceof Date)) {
      this.endDate = new Date(this.endDate);
    }
  }

  private getLastMonday(): Date {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    let daysToSubtract;
    if (dayOfWeek === 1) {
      // If today is Monday, go back 7 days to get last Monday
      daysToSubtract = 7;
    } else if (dayOfWeek === 0) {
      // If today is Sunday, go back 6 days to get last Monday
      daysToSubtract = 6;
    } else {
      // For other days, go back to the previous Monday
      daysToSubtract = dayOfWeek - 1;
    }
    
    const lastMonday = new Date(today);
    lastMonday.setDate(today.getDate() - daysToSubtract);
    return lastMonday;
  }

  openModal(): void {
    this.loading = true;
    this.startDateLicenses = undefined;
    this.endDateLicenses = undefined;
    this.resetSummaries();
    
    this.modalService.open(this.modal, { size: 'xl' });
    this.fetchLicenseData();
  }

  private fetchLicenseData(): void {
    if (!this.allLicenses || this.allLicenses.length === 0) {
      console.warn('No licenses available for analysis');
      this.loading = false;
      return;
    }
    
    // Ensure dates are properly converted to Date objects
    const startDate = this.ensureDateObject(this.startDate);
    const endDate = this.ensureDateObject(this.endDate);
    
    // Use the service to filter licenses for start date
    this.startDateLicenses = this.licenseFilteringService.filterAndSortLicenses(
      this.allLicenses,
      {
        asOfDate: startDate,
        ignoreLicenseDates: false,
        gracePeriodDays: 60
      }
    );
    this.calculateStartDateSummary();

    // Use the service to filter licenses for end date
    this.endDateLicenses = this.licenseFilteringService.filterAndSortLicenses(
      this.allLicenses,
      {
        asOfDate: endDate,
        ignoreLicenseDates: false,
        gracePeriodDays: 60
      }
    );
    this.calculateEndDateSummary();

    // Calculate changes and stop loading
    this.calculateChanges();
    this.loading = false;
  }


  private calculateStartDateSummary(): void {
    this.startDateSummary = this.calculateSummary(this.startDateLicenses);
  }

  private calculateEndDateSummary(): void {
    this.endDateSummary = this.calculateSummary(this.endDateLicenses);
  }

  private calculateSummary(licenses: PurchasedLicensesInfo[]): LicenseChangeSummary {
    return {
      purchaseCount: licenses.length,
      licenseCount: licenses.reduce((sum, license) => sum + (license.Quantity || 0), 0),
      totalDollarAmount: licenses.reduce((sum, license) => sum + (license.PurchaseDollarAmount || 0), 0),
      arrDollarAmount: licenses.reduce((sum, license) => sum + (license.ARR || 0), 0)
    };
  }

  private calculateChanges(): void {
    // Find expired licenses (in start date but not in end date)
    this.expiredLicenses = this.startDateLicenses.filter(startLicense => 
      !this.endDateLicenses.some(endLicense => 
        endLicense.PurchasedLicensesID === startLicense.PurchasedLicensesID
      )
    );
    
    // Sort expired licenses by PurchasingOrganizationDescription
    this.expiredLicenses.sort((a, b) => {
      const aDesc = a.PurchasingOrganizationDescription || '';
      const bDesc = b.PurchasingOrganizationDescription || '';
      return aDesc.localeCompare(bDesc);
    });
    
    this.expiredSummary = this.calculateSummary(this.expiredLicenses);

    // Find new licenses (in end date but not in start date)
    this.newLicenses = this.endDateLicenses.filter(endLicense => 
      !this.startDateLicenses.some(startLicense => 
        startLicense.PurchasedLicensesID === endLicense.PurchasedLicensesID
      )
    );
    
    // Sort new licenses by PurchasingOrganizationDescription
    this.newLicenses.sort((a, b) => {
      const aDesc = a.PurchasingOrganizationDescription || '';
      const bDesc = b.PurchasingOrganizationDescription || '';
      return aDesc.localeCompare(bDesc);
    });
    
    this.newSummary = this.calculateSummary(this.newLicenses);
  }

  private resetSummaries(): void {
    this.startDateSummary = { purchaseCount: 0, licenseCount: 0, totalDollarAmount: 0, arrDollarAmount: 0 };
    this.expiredSummary = { purchaseCount: 0, licenseCount: 0, totalDollarAmount: 0, arrDollarAmount: 0 };
    this.newSummary = { purchaseCount: 0, licenseCount: 0, totalDollarAmount: 0, arrDollarAmount: 0 };
    this.endDateSummary = { purchaseCount: 0, licenseCount: 0, totalDollarAmount: 0, arrDollarAmount: 0 };
  }

  private ensureDateObject(date: any): Date {
    if (date instanceof Date) {
      return date;
    }
    if (typeof date === 'string') {
      return new Date(date);
    }
    if (date && typeof date === 'object' && date.year && date.month && date.day) {
      // Handle NgbDateStruct format
      return new Date(date.year, date.month - 1, date.day);
    }
    return new Date();
  }

  private formatDateForAPI(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onDateChange(): void {
    // Ensure dates are properly converted to Date objects
    if (this.startDate && this.endDate) {
      const startDate = this.ensureDateObject(this.startDate);
      const endDate = this.ensureDateObject(this.endDate);
      
      if (startDate <= endDate) {
        this.loading = true;
        this.fetchLicenseData();
      }
    }
  }

  formatCurrency(amount: number): string {
    if (amount === null || amount === undefined) return '$0';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  }

  formatNumber(num: number): string {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString();
  }

  cancel(): void {
    this.modalService.dismissAll();
  }
}
